import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Typography, Spin, Input, Switch, ConfigProvider, Alert, theme } from 'antd';
import { IconCamera, IconRefresh, IconCheck, IconAlertTriangle, IconMapPin } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import AppLayout from '../../components/Layouts/Layout';
import PageColumn from '../../components/Layouts/PageColumn';
import EmptyState from '../../components/EmptyState';
import BookingReferenceTag from '../../components/BookingReferenceTag';
import MachineryOutOfServiceModal from '../../components/MachineryOutOfServiceModal';
import { tealAccentTheme } from '../../theme/appTheme';
import ConfirmBottomSheet from '../../components/ConfirmBottomSheet';
import PillNumberField from '../../components/PillNumberField';
import HandoverAcknowledgementSheet from '../../components/HandoverAcknowledgementSheet';
import { message } from '../../services/toastMessage';
import { getBookingById } from '../../services/BookingService';
import {
  getCheckInOutReports,
  submitCheckInOutReport,
  captureBookingAtHandover,
  getCheckInOutPhotoObjectUrl,
  ALREADY_SUBMITTED_MARKER,
} from '../../services/CheckInOutService';
import { IBooking } from '../../interfaces/heavyDutyMachinery/IBooking';
import { CheckInOutReportType, ICheckInOutReport } from '../../interfaces/heavyDutyMachinery/ICheckInOut';
import { useAuth } from '../../contextProviders/AuthProvider';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { compressPhoto } from '../../helpers/compressPhoto';
import GearSpinner from '../../components/GearSpinner';
import { TEAL, TEAL_SOFT_BG, SURFACE_1_SOFT, CORAL, pillToneStyle } from '../../theme/creamyTokens';
import { ACK_OUTCOME_LABEL_KEYS, ACK_OUTCOME_TONES } from '../../constants/acknowledgementOutcomes';

const { Text } = Typography;
const { TextArea } = Input;

type PhotoSlotKey = 'front' | 'back' | 'leftSide' | 'rightSide' | 'cabinInterior' | 'hourMeter';

const PHOTO_SLOTS: { key: PhotoSlotKey; labelKey: string }[] = [
  { key: 'front', labelKey: TRANSLATIONS.CHECK_IN_OUT_SLOT_FRONT },
  { key: 'back', labelKey: TRANSLATIONS.CHECK_IN_OUT_SLOT_BACK },
  { key: 'leftSide', labelKey: TRANSLATIONS.CHECK_IN_OUT_SLOT_LEFT_SIDE },
  { key: 'rightSide', labelKey: TRANSLATIONS.CHECK_IN_OUT_SLOT_RIGHT_SIDE },
  { key: 'cabinInterior', labelKey: TRANSLATIONS.CHECK_IN_OUT_SLOT_CABIN_INTERIOR },
  { key: 'hourMeter', labelKey: TRANSLATIONS.CHECK_IN_OUT_SLOT_HOUR_METER },
];

interface PhotoSlotState {
  file: File | null;
  previewUrl: string | null;
  /**
   * True while the shot is being shrunk (see compressPhoto). A fraction of
   * a second on a recent phone and a couple of seconds on an old one, which
   * is long enough that a tile with nothing in it looks like a camera that
   * failed to open.
   */
  processing?: boolean;
}

const emptySlots = (): Record<PhotoSlotKey, PhotoSlotState> => ({
  front: { file: null, previewUrl: null },
  back: { file: null, previewUrl: null },
  leftSide: { file: null, previewUrl: null },
  rightSide: { file: null, previewUrl: null },
  cabinInterior: { file: null, previewUrl: null },
  hourMeter: { file: null, previewUrl: null },
});

type FlowStep = 'intro' | 'photos' | 'data' | 'review';
type LocationStatus = 'idle' | 'capturing' | 'captured' | 'error';


/** One evidence photo, fetched (and object-URL'd) lazily -- the submitted
 * report's own thumbnails/full view in the read-only summary. */
const ReportPhotoThumb: React.FC<{ bookingId: string; reportType: CheckInOutReportType; photoId: string }> = ({ bookingId, reportType, photoId }) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    getCheckInOutPhotoObjectUrl(bookingId, reportType, photoId)
      .then((u) => {
        if (cancelled) {
          window.URL.revokeObjectURL(u);
          return;
        }
        objectUrl = u;
        setUrl(u);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [bookingId, reportType, photoId]);

  return (
    <div style={{ aspectRatio: '1', borderRadius: 12, overflow: 'hidden', background: SURFACE_1_SOFT }}>
      {url ? (
        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin size="small" />
        </div>
      )}
    </div>
  );
};

/**
 * Fase 6: guided digital check-in/check-out capture -- 6 predefined photo
 * angles, engine hours/fuel %, a damage claim step (CheckOut, host-only --
 * never shown to a guest submitting their own CheckOut, see
 * CheckInOutController's own doc comment on the backend for why), a final
 * confirmation that spells out the report's immutability before sending,
 * and resilient upload: photos stay in local state (never cleared) across a
 * failed submit, so "Reintentar" resends without retaking anything. One
 * page for both CheckIn and CheckOut (:reportType) and both roles (host/
 * guest, derived from booking.guestUserId -- see isHost below), since the
 * shape of the flow is otherwise identical.
 */
const CheckInOutFlow: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { id, reportType: reportTypeParam } = useParams<{ id: string; reportType: string }>();
  const { userId } = useAuth();
  const reportType = reportTypeParam as CheckInOutReportType;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [booking, setBooking] = useState<IBooking | null>(null);
  const [existingReport, setExistingReport] = useState<ICheckInOutReport | null>(null);

  const [step, setStep] = useState<FlowStep>('intro');
  const [photoSlots, setPhotoSlots] = useState<Record<PhotoSlotKey, PhotoSlotState>>(emptySlots());
  const [engineHours, setEngineHours] = useState<number | null>(null);
  const [fuelPercent, setFuelPercent] = useState<number | null>(null);
  const [hasDamage, setHasDamage] = useState(false);
  // Asked once, right after the host signs the return note. That is the
  // exact moment they are standing in front of the machine knowing whether
  // it is fit to go out again -- and the moment a host told us they had no
  // way to say so, which is how another booking ended up on a machine that
  // was never going to make it.
  const [askOperational, setAskOperational] = useState(false);
  const [parkOpen, setParkOpen] = useState(false);
  const [damageNotes, setDamageNotes] = useState('');
  const [damageClaimAmount, setDamageClaimAmount] = useState<number | null>(null);

  const [location, setLocation] = useState<{ lat?: number; lng?: number; status: LocationStatus }>({ status: 'idle' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [acknowledgeOpen, setAcknowledgeOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeSlotKeyRef = useRef<PhotoSlotKey | null>(null);

  const isHost = !!booking && !!userId && booking.guestUserId !== userId;
  // Host: always. Guest: only for a CheckOut on a dry-hire booking (no
  // Operator involved) -- same rule CheckInOutController enforces on the
  // backend, mirrored here so an unauthorized guest never even sees the flow.
  const canSubmit =
    !!booking && (isHost || (reportType === 'CheckOut' && !booking.operatorId));
  // But SUBMITTING isn't the only reason to be on this page. Once a report
  // exists, the other party has to be able to look at the photos and the
  // readings before putting their name to it -- that reading-and-answering
  // is the second half of the albarán, and refusing them the page would
  // leave them accepting a delivery note they were never shown.
  const isAuthorized = canSubmit || !!existingReport;

  const awaitingMyAnswer =
    !!existingReport && !existingReport.acknowledgementOutcome && !!userId && existingReport.submittedByUserId !== userId;

  useEffect(() => {
    if (!id || !reportType) return;
    setLoading(true);
    setLoadError(false);
    Promise.all([getBookingById(id), getCheckInOutReports(id)])
      .then(([bookingResult, reports]) => {
        setBooking(bookingResult);
        setExistingReport(reports.find((r) => r.reportType === reportType) || null);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [id, reportType]);

  // Revoke every local object URL on unmount -- these are createObjectURL'd
  // straight from the camera/file picker, never uploaded until final submit.
  useEffect(
    () => () => {
      Object.values(photoSlots).forEach((slot) => {
        if (slot.previewUrl) window.URL.revokeObjectURL(slot.previewUrl);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const captureLocation = () => {
    if (!('geolocation' in navigator)) {
      setLocation({ status: 'error' });
      return;
    }
    setLocation({ status: 'capturing' });
    navigator.geolocation.getCurrentPosition(
      (position) => setLocation({ lat: position.coords.latitude, lng: position.coords.longitude, status: 'captured' }),
      () => setLocation({ status: 'error' }),
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const openCameraFor = (key: PhotoSlotKey) => {
    activeSlotKeyRef.current = key;
    fileInputRef.current?.click();
  };

  // Shrunk here, as each shot is taken, rather than all six at submit time.
  // Two reasons: the wait is split into six short ones instead of one long
  // freeze at the end, when the customer is standing there waiting to sign;
  // and what sits in state is already the small file, so a retake replaces
  // 400 KB instead of 5 MB. compressPhoto never throws and returns the
  // original when it cannot help, so the slot is filled either way.
  const handleFileSelected: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0];
    const key = activeSlotKeyRef.current;
    if (file && key) {
      setPhotoSlots((prev) => {
        if (prev[key].previewUrl) window.URL.revokeObjectURL(prev[key].previewUrl!);
        return { ...prev, [key]: { file: null, previewUrl: null, processing: true } };
      });
      void compressPhoto(file).then((ready) => {
        setPhotoSlots((prev) => {
          if (prev[key].previewUrl) window.URL.revokeObjectURL(prev[key].previewUrl!);
          return { ...prev, [key]: { file: ready, previewUrl: URL.createObjectURL(ready), processing: false } };
        });
      });
    }
    // Reset so picking the same file again (a genuine retake with an
    // unchanged shot) still fires onChange.
    e.target.value = '';
  };

  const photosCompleteCount = PHOTO_SLOTS.filter((slot) => photoSlots[slot.key].file).length;
  const allPhotosReady = photosCompleteCount === PHOTO_SLOTS.length;

  // A charge-at-handover booking whose money has not been taken yet. The
  // capture happens before the walkaround rather than after the signature,
  // so a declined card costs a wasted trip instead of a machine handed over
  // for free -- see BookingCaptureHelper for the full reasoning.
  const needsCapture =
    reportType === 'CheckIn' &&
    booking?.captureMode === 'OnHandover' &&
    booking?.paymentStatus !== 'Succeeded';

  const startFlow = async () => {
    if (needsCapture && id) {
      setSubmitting(true);
      setSubmitError(null);
      try {
        await captureBookingAtHandover(id);
        message.success(translate(t, TRANSLATIONS.CHECK_IN_OUT_CAPTURE_DONE));
        // Re-read so the screen stops offering to charge again, and so
        // anything downstream sees the booking as paid.
        setBooking(await getBookingById(id));
      } catch {
        // Deliberately blocking: the host must not be able to walk past this
        // into the photo steps. The only way on is a successful charge.
        setSubmitError(translate(t, TRANSLATIONS.CHECK_IN_OUT_CAPTURE_ERROR));
        return;
      } finally {
        setSubmitting(false);
      }
    }
    setStep('photos');
    captureLocation();
  };

  const doSubmit = async () => {
    if (!id) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const files = PHOTO_SLOTS.map((slot) => photoSlots[slot.key].file as File);
      const result = await submitCheckInOutReport(
        id,
        reportType,
        {
          fuelPercent: fuelPercent ?? undefined,
          engineHours: engineHours ?? undefined,
          damageNotes: reportType === 'CheckOut' && isHost && hasDamage && damageNotes.trim() ? damageNotes.trim() : undefined,
          damageClaimAmount: reportType === 'CheckOut' && isHost && hasDamage ? damageClaimAmount ?? undefined : undefined,
          latitude: location.lat ?? 0,
          longitude: location.lng ?? 0,
        },
        files
      );
      setExistingReport(result);
      message.success(translate(t, TRANSLATIONS.CHECK_IN_OUT_SUBMIT_SUCCESS));
      if (reportType === 'CheckOut' && isHost) setAskOperational(true);
    } catch (error: any) {
      const errMessage = typeof error?.message === 'string' ? error.message : '';
      if (errMessage.includes(ALREADY_SUBMITTED_MARKER)) {
        // A previous attempt actually succeeded server-side -- the response
        // just never made it back (e.g. connection dropped mid-upload).
        // Not a failure: fetch the real report and show it as submitted.
        try {
          const reports = await getCheckInOutReports(id);
          const already = reports.find((r) => r.reportType === reportType);
          if (already) {
            setExistingReport(already);
            message.success(translate(t, TRANSLATIONS.CHECK_IN_OUT_SUBMIT_SUCCESS));
            return;
          }
        } catch {
          // fall through to the generic error below
        }
      }
      // Always the friendly copy, never the raw fetch/backend error text --
      // a submission here only realistically fails on connectivity, which
      // this message already explains (and reassures the photos are safe).
      setSubmitError(translate(t, TRANSLATIONS.CHECK_IN_OUT_SUBMIT_ERROR));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmSubmit = () => {
    setConfirmOpen(false);
    doSubmit();
  };

  const title = translate(t, reportType === 'CheckIn' ? TRANSLATIONS.CHECK_IN_OUT_TITLE_CHECK_IN : TRANSLATIONS.CHECK_IN_OUT_TITLE_CHECK_OUT);

  if (loading) {
    return (
      <AppLayout>
        <div style={{ textAlign: 'center', padding: 64 }}>
          <Spin size="large" />
        </div>
      </AppLayout>
    );
  }

  if (loadError || !booking) {
    return (
      <AppLayout>
        <EmptyState icon={<IconAlertTriangle size={24} />} title={translate(t, TRANSLATIONS.CHECK_IN_OUT_LOAD_ERROR)} description="" />
      </AppLayout>
    );
  }

  if (!isAuthorized) {
    return (
      <AppLayout>
        <EmptyState icon={<IconAlertTriangle size={24} />} title={translate(t, TRANSLATIONS.CHECK_IN_OUT_NOT_AUTHORIZED)} description="" />
      </AppLayout>
    );
  }

  // Already submitted (loaded that way, or just submitted this session) --
  // read-only summary, no edit path exists anywhere in this flow.
  if (existingReport) {
    const canSeeDamage = isHost;
    return (
      <AppLayout>
        <PageColumn>
          <Text strong style={{ fontSize: 20, display: 'block', marginBottom: 4 }}>
            {title}
          </Text>
          {/* The paper this stands in for carries a reference at the top; this is
              where somebody on site checks they are signing off the right
              machine on the right rental. */}
          <BookingReferenceTag reference={booking.reference} style={{ display: 'block', marginBottom: 4 }} />
          <Card bordered={false} style={{ borderRadius: 18, background: SURFACE_1_SOFT, marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ display: 'flex', width: 32, height: 32, borderRadius: '50%', background: TEAL_SOFT_BG, color: TEAL, alignItems: 'center', justifyContent: 'center' }}>
                <IconCheck size={18} />
              </span>
              <Text strong>{translate(t, TRANSLATIONS.CHECK_IN_OUT_ALREADY_SUBMITTED_TITLE)}</Text>
            </div>
            {/* Not a blocking dialog: the answer is usually "yes" and the
                report is already filed either way. It is a question, put
                where the host is already looking, and it goes away once
                answered. */}
            {askOperational && (
              <div style={{ borderRadius: 14, padding: '12px 14px', background: '#F6EDD5', marginBottom: 12 }}>
                <Text strong style={{ display: 'block', marginBottom: 2 }}>
                  {translate(t, TRANSLATIONS.CHECK_OUT_STILL_OPERATIONAL_TITLE)}
                </Text>
                <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 10 }}>
                  {translate(t, TRANSLATIONS.CHECK_OUT_STILL_OPERATIONAL_DESCRIPTION)}
                </Text>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button shape="round" onClick={() => setAskOperational(false)}>
                    {translate(t, TRANSLATIONS.CHECK_OUT_STILL_OPERATIONAL_YES)}
                  </Button>
                  <Button shape="round" type="primary" onClick={() => setParkOpen(true)}>
                    {translate(t, TRANSLATIONS.CHECK_OUT_STILL_OPERATIONAL_NO)}
                  </Button>
                </div>
              </div>
            )}
            {parkOpen && booking.heavyDutyMachineryId && (
              <MachineryOutOfServiceModal
                open
                machineryId={booking.heavyDutyMachineryId}
                machineryTitle={booking.heavyDutyMachineryTitle}
                onClose={() => setParkOpen(false)}
                onParked={() => {
                  setParkOpen(false);
                  setAskOperational(false);
                }}
              />
            )}
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              {translate(t, TRANSLATIONS.CHECK_IN_OUT_ALREADY_SUBMITTED_AT, { date: dayjs(existingReport.submittedAt).format('DD/MM/YYYY HH:mm') })}
            </Text>
            {existingReport.engineHoursAtReport != null && (
              <Text style={{ display: 'block' }}>
                {translate(t, TRANSLATIONS.CHECK_IN_OUT_ENGINE_HOURS_LABEL)}: {existingReport.engineHoursAtReport}
              </Text>
            )}
            {existingReport.fuelPercentAtReport != null && (
              <Text style={{ display: 'block' }}>
                {translate(t, TRANSLATIONS.CHECK_IN_OUT_FUEL_PERCENT_LABEL)}: {existingReport.fuelPercentAtReport}%
              </Text>
            )}
            {canSeeDamage && (
              <Text style={{ display: 'block', marginTop: 8 }}>
                {existingReport.damageClaimAmount
                  ? translate(t, TRANSLATIONS.CHECK_IN_OUT_REVIEW_DAMAGE_CLAIM, { amount: existingReport.damageClaimAmount.toFixed(2) })
                  : translate(t, TRANSLATIONS.CHECK_IN_OUT_REVIEW_NO_DAMAGE)}
              </Text>
            )}
            {canSeeDamage && existingReport.damageNotes && (
              <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 13 }}>
                {existingReport.damageNotes}
              </Text>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 16 }}>
              {existingReport.photos.map((photo) => (
                <ReportPhotoThumb key={photo.id} bookingId={booking.id} reportType={reportType} photoId={photo.id} />
              ))}
            </div>

            {/* The other party's answer, once there is one -- the half of
                the albarán that turns it from one person's account into
                something both sides agreed to. */}
            {existingReport.acknowledgementOutcome && (
              <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${token.colorBorderSecondary}` }}>
                <span
                  style={{
                    display: 'inline-flex',
                    padding: '3px 10px',
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 600,
                    ...pillToneStyle(ACK_OUTCOME_TONES[existingReport.acknowledgementOutcome], token),
                  }}
                >
                  {translate(t, ACK_OUTCOME_LABEL_KEYS[existingReport.acknowledgementOutcome])}
                </span>
                {existingReport.acknowledgementRemarks && (
                  <Text style={{ display: 'block', marginTop: 8, fontSize: 13 }}>{existingReport.acknowledgementRemarks}</Text>
                )}
              </div>
            )}
          </Card>

          {awaitingMyAnswer && (
            <div style={{ marginTop: 16 }}>
              <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 10 }}>
                {translate(t, TRANSLATIONS.HANDOVER_ACK_PROMPT)}
              </Text>
              <Button
                type="primary"
                shape="round"
                block
                size="large"
                onClick={() => setAcknowledgeOpen(true)}
              >
                {translate(t, TRANSLATIONS.HANDOVER_ACK_OPEN_BUTTON)}
              </Button>
            </div>
          )}

          <ConfigProvider theme={tealAccentTheme}>
            <Button
              type={awaitingMyAnswer ? 'default' : 'primary'}
              shape="round"
              block
              size="large"
              style={{ marginTop: awaitingMyAnswer ? 10 : 20 }}
              onClick={() => navigate(-1)}
            >
              {translate(t, TRANSLATIONS.CHECK_IN_OUT_DONE_BUTTON)}
            </Button>
          </ConfigProvider>

          <HandoverAcknowledgementSheet
            bookingId={booking.id}
            reportType={reportType}
            open={acknowledgeOpen}
            onClose={() => setAcknowledgeOpen(false)}
            onAcknowledged={setExistingReport}
          />
        </PageColumn>
      </AppLayout>
    );
  }

  const renderIntro = () => (
    <Card bordered={false} style={{ borderRadius: 18, background: SURFACE_1_SOFT }}>
      <Text style={{ display: 'block', marginBottom: 20, lineHeight: 1.6 }}>
        {translate(t, reportType === 'CheckIn' ? TRANSLATIONS.CHECK_IN_OUT_INTRO_CHECK_IN : TRANSLATIONS.CHECK_IN_OUT_INTRO_CHECK_OUT)}
      </Text>
      {/* Said before the button, not after a failure: a host about to start
          unloading deserves to know the charge is happening first and what it
          means if it does not go through. */}
      {needsCapture && (
        <Alert
          type="info"
          showIcon
          message={translate(t, TRANSLATIONS.CHECK_IN_OUT_CAPTURE_NOTICE)}
          style={{ borderRadius: 14, marginBottom: 16 }}
        />
      )}
      {submitError && (
        <Alert type="error" showIcon message={submitError} style={{ borderRadius: 14, marginBottom: 16 }} />
      )}
      <ConfigProvider theme={tealAccentTheme}>
        <Button type="primary" shape="round" block size="large" loading={submitting} onClick={startFlow}>
          {translate(
            t,
            needsCapture ? TRANSLATIONS.CHECK_IN_OUT_CAPTURE_BUTTON : TRANSLATIONS.CHECK_IN_OUT_START_BUTTON
          )}
        </Button>
      </ConfigProvider>
    </Card>
  );

  const renderPhotosStep = () => (
    <>
      <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 4 }}>
        {translate(t, TRANSLATIONS.CHECK_IN_OUT_PHOTOS_STEP_TITLE)}
      </Text>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 13 }}>
        {translate(t, TRANSLATIONS.CHECK_IN_OUT_PHOTOS_STEP_SUBTITLE)}
      </Text>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {PHOTO_SLOTS.map((slot) => {
          const state = photoSlots[slot.key];
          const label = translate(t, slot.labelKey);
          return (
            <button
              key={slot.key}
              type="button"
              onClick={() => openCameraFor(slot.key)}
              style={{
                position: 'relative',
                aspectRatio: '1',
                border: state.file ? `2px solid ${TEAL}` : `2px dashed ${token.colorBorder}`,
                borderRadius: 16,
                background: state.previewUrl ? `url(${state.previewUrl}) center/cover no-repeat` : SURFACE_1_SOFT,
                padding: 0,
                cursor: 'pointer',
                overflow: 'hidden',
              }}
            >
              {!state.file && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 6 }}>
                  {/* An empty tile and a busy tile look the same otherwise,
                      and on an older phone shrinking a shot takes long
                      enough that the difference matters -- somebody who
                      thinks the camera failed takes the photo again. */}
                  {state.processing ? (
                    <GearSpinner size={22} style={{ color: token.colorTextSecondary }} />
                  ) : (
                    <IconCamera size={22} color={token.colorTextSecondary} />
                  )}
                  <Text type="secondary" style={{ fontSize: 12, textAlign: 'center', padding: '0 6px' }}>
                    {state.processing ? translate(t, TRANSLATIONS.CHECK_IN_OUT_PHOTO_PREPARING) : label}
                  </Text>
                </div>
              )}
              {state.file && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    fontSize: 11,
                    padding: '4px 6px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>{label}</span>
                  <IconRefresh size={14} aria-label={translate(t, TRANSLATIONS.CHECK_IN_OUT_RETAKE_LABEL)} />
                </div>
              )}
            </button>
          );
        })}
      </div>
      {!allPhotosReady && (
        <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 13 }}>
          {translate(t, TRANSLATIONS.CHECK_IN_OUT_PHOTOS_MISSING_HINT, { count: PHOTO_SLOTS.length - photosCompleteCount })}
        </Text>
      )}
      <ConfigProvider theme={tealAccentTheme}>
        <Button type="primary" shape="round" block size="large" disabled={!allPhotosReady} style={{ marginTop: 20 }} onClick={() => setStep('data')}>
          {translate(t, TRANSLATIONS.CHECK_IN_OUT_CONTINUE_BUTTON)}
        </Button>
        <Button shape="round" block style={{ marginTop: 8 }} onClick={() => setStep('intro')}>
          {translate(t, TRANSLATIONS.CHECK_IN_OUT_BACK_BUTTON)}
        </Button>
      </ConfigProvider>
    </>
  );

  const showDamageSection = reportType === 'CheckOut' && isHost;

  const renderDataStep = () => (
    <>
      <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 16 }}>
        {translate(t, TRANSLATIONS.CHECK_IN_OUT_DATA_STEP_TITLE)}
      </Text>
      <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
        <div style={{ flex: 1 }}>
          <PillNumberField
            label={translate(t, TRANSLATIONS.CHECK_IN_OUT_ENGINE_HOURS_LABEL)}
            min={0}
            step={0.1}
            value={engineHours ?? undefined}
            onChange={(v) => setEngineHours(v)}
          />
        </div>
        <div style={{ flex: 1 }}>
          <PillNumberField
            label={translate(t, TRANSLATIONS.CHECK_IN_OUT_FUEL_PERCENT_LABEL)}
            min={0}
            step={1}
            value={fuelPercent ?? undefined}
            onChange={(v) => setFuelPercent(v)}
          />
        </div>
      </div>

      {showDamageSection && (
        <Card bordered={false} style={{ borderRadius: 14, background: hasDamage ? '#FAECE7' : SURFACE_1_SOFT, marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong>{translate(t, TRANSLATIONS.CHECK_IN_OUT_DAMAGE_TOGGLE_LABEL)}</Text>
            <Switch checked={hasDamage} onChange={setHasDamage} />
          </div>
          {hasDamage && (
            <div style={{ marginTop: 16 }}>
              <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
                {translate(t, TRANSLATIONS.CHECK_IN_OUT_DAMAGE_NOTES_LABEL)}
              </Text>
              <TextArea
                rows={3}
                value={damageNotes}
                onChange={(e) => setDamageNotes(e.target.value)}
                placeholder={translate(t, TRANSLATIONS.CHECK_IN_OUT_DAMAGE_NOTES_PLACEHOLDER)}
                style={{ borderRadius: 12, marginBottom: 16 }}
              />
              <PillNumberField
                label={translate(t, TRANSLATIONS.CHECK_IN_OUT_DAMAGE_CLAIM_AMOUNT_LABEL)}
                min={0}
                step={0.01}
                value={damageClaimAmount ?? undefined}
                onChange={(v) => setDamageClaimAmount(v)}
              />
            </div>
          )}
        </Card>
      )}

      <ConfigProvider theme={tealAccentTheme}>
        <Button type="primary" shape="round" block size="large" style={{ marginTop: 12 }} onClick={() => setStep('review')}>
          {translate(t, TRANSLATIONS.CHECK_IN_OUT_CONTINUE_BUTTON)}
        </Button>
        <Button shape="round" block style={{ marginTop: 8 }} onClick={() => setStep('photos')}>
          {translate(t, TRANSLATIONS.CHECK_IN_OUT_BACK_BUTTON)}
        </Button>
      </ConfigProvider>
    </>
  );

  const renderReviewStep = () => (
    <>
      <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 16 }}>
        {translate(t, TRANSLATIONS.CHECK_IN_OUT_REVIEW_STEP_TITLE)}
      </Text>

      <Card bordered={false} style={{ borderRadius: 18, background: SURFACE_1_SOFT, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6, marginBottom: 12 }}>
          {PHOTO_SLOTS.map((slot) => (
            <div key={slot.key} style={{ aspectRatio: '1', borderRadius: 8, background: `url(${photoSlots[slot.key].previewUrl}) center/cover no-repeat` }} />
          ))}
        </div>
        <Text type="secondary" style={{ display: 'block' }}>
          {translate(t, TRANSLATIONS.CHECK_IN_OUT_REVIEW_PHOTOS_COUNT, { count: photosCompleteCount })}
        </Text>
        {engineHours != null && (
          <Text style={{ display: 'block', marginTop: 8 }}>
            {translate(t, TRANSLATIONS.CHECK_IN_OUT_ENGINE_HOURS_LABEL)}: {engineHours}
          </Text>
        )}
        {fuelPercent != null && (
          <Text style={{ display: 'block' }}>
            {translate(t, TRANSLATIONS.CHECK_IN_OUT_FUEL_PERCENT_LABEL)}: {fuelPercent}%
          </Text>
        )}
        {showDamageSection && (
          <Text style={{ display: 'block', marginTop: 8, fontWeight: hasDamage ? 600 : undefined, color: hasDamage ? CORAL : undefined }}>
            {hasDamage && damageClaimAmount
              ? translate(t, TRANSLATIONS.CHECK_IN_OUT_REVIEW_DAMAGE_CLAIM, { amount: damageClaimAmount.toFixed(2) })
              : translate(t, TRANSLATIONS.CHECK_IN_OUT_REVIEW_NO_DAMAGE)}
          </Text>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12 }}>
          <IconMapPin size={14} color={token.colorTextSecondary} />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {location.status === 'capturing' && translate(t, TRANSLATIONS.CHECK_IN_OUT_LOCATION_CAPTURING)}
            {location.status === 'captured' && translate(t, TRANSLATIONS.CHECK_IN_OUT_LOCATION_CAPTURED)}
            {location.status === 'error' && translate(t, TRANSLATIONS.CHECK_IN_OUT_LOCATION_ERROR)}
          </Text>
          {location.status === 'error' && (
            <Button size="small" type="link" onClick={captureLocation} style={{ padding: 0, height: 'auto' }}>
              {translate(t, TRANSLATIONS.CHECK_IN_OUT_LOCATION_RETRY_BUTTON)}
            </Button>
          )}
        </div>
      </Card>

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 16, padding: '0 4px' }}>
        <IconAlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2, color: token.colorWarning }} />
        <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>
          {translate(t, TRANSLATIONS.CHECK_IN_OUT_IMMUTABLE_WARNING)}
        </Text>
      </div>

      {submitError && (
        <div style={{ marginBottom: 16, padding: 12, borderRadius: 12, background: '#FDECEA' }}>
          <Text type="danger" style={{ fontSize: 13, display: 'block' }}>
            {submitError}
          </Text>
        </div>
      )}

      <ConfigProvider theme={tealAccentTheme}>
        <Button
          type="primary"
          shape="round"
          block
          size="large"
          loading={submitting}
          disabled={location.status !== 'captured'}
          onClick={() => (submitError ? doSubmit() : setConfirmOpen(true))}
        >
          {submitting
            ? translate(t, TRANSLATIONS.CHECK_IN_OUT_UPLOADING)
            : submitError
              ? translate(t, TRANSLATIONS.CHECK_IN_OUT_RETRY_BUTTON)
              : translate(t, TRANSLATIONS.CHECK_IN_OUT_SUBMIT_BUTTON)}
        </Button>
        {!submitting && (
          <Button shape="round" block style={{ marginTop: 8 }} onClick={() => setStep('data')}>
            {translate(t, TRANSLATIONS.CHECK_IN_OUT_BACK_BUTTON)}
          </Button>
        )}
      </ConfigProvider>
    </>
  );

  return (
    <AppLayout>
      <PageColumn>
        <Text strong style={{ fontSize: 20, display: 'block', marginBottom: 2 }}>
          {title}
        </Text>
        <BookingReferenceTag reference={booking.reference} style={{ display: 'block', marginBottom: 16 }} />

        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png" capture="environment" style={{ display: 'none' }} onChange={handleFileSelected} />

        {step === 'intro' && renderIntro()}
        {step === 'photos' && renderPhotosStep()}
        {step === 'data' && renderDataStep()}
        {step === 'review' && renderReviewStep()}
      </PageColumn>

      <ConfirmBottomSheet
        open={confirmOpen}
        title={translate(t, TRANSLATIONS.CHECK_IN_OUT_CONFIRM_SHEET_TITLE)}
        description={translate(t, TRANSLATIONS.CHECK_IN_OUT_CONFIRM_SHEET_DESCRIPTION)}
        confirmText={translate(t, TRANSLATIONS.CHECK_IN_OUT_CONFIRM_SHEET_CONFIRM)}
        cancelText={translate(t, TRANSLATIONS.CHECK_IN_OUT_CONFIRM_SHEET_CANCEL)}
        onConfirm={handleConfirmSubmit}
        onClose={() => setConfirmOpen(false)}
      />
    </AppLayout>
  );
};

export default CheckInOutFlow;
